import { useState, useEffect, useRef } from 'react';
// store
import { useSelector } from 'store';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from 'utils/format';
// assets
import { ReactComponent as Recently } from 'assets/real-time/recently.svg';
// styles
import { RealTimeContent, LocationChart } from 'styles';
// apis
import { getRecentlyDevices } from 'apis';

// ----------------------------------------------------------------------

interface Props {
  data: any[];
  group: any;
  setRecently: React.Dispatch<any>;
};

// ----------------------------------------------------------------------

export default function ContentDevices({ data, group, setRecently }: Props) {
  const devices = useSelector((state) => state.devices);

  const [ preprocessing, setPreprocessing ] = useState([]); // Data Preprocessing
  const [ processed, setProcessed ] = useState([]); // Processed Data

  const datasets = useRef(null);

  useEffect(() => {
    if ( devices.length > 0 ) {
      let dataset: any[] = [];
      devices.forEach((values: any) => {
        const rawData = data.find((item: any) => item.iotDeviceId === values.iotDeviceId);
        if ( rawData ) {
          let sets = { ...values, ...rawData };
          let waterQualities: any[] = [];
          let predictions: any[] = [];
          values.waterQualitySensors.forEach((value: any) => {
            if ( value.waterQualitySensorId ) {
              const sensor = rawData.waterQualities.find((item: any) => item.waterQualitySensorId === value.waterQualitySensorId);
              const merge = sensor ? { ...value, ...sensor } : { ...value };
              waterQualities.push(merge);
            }
          });
          values.waterQualityPredictions.forEach((value: any) => {
            if ( value.waterQualityPredictionId ) {
              const prediction = rawData.waterQualityPredictions.find((item: any) => item.waterQualityPredictionId === value.waterQualityPredictionId);
              const merge = prediction ? { ...value, ...prediction } : { ...value };
              predictions.push(merge);
            }
          });
          sets.waterQualities = waterQualities;
          sets.waterQualityPredictions = predictions;
          delete sets.waterQualitySensors;
          dataset.push(sets);
        }
      });
      datasets.current = dataset;
      setPreprocessing(dataset);
    }
  }, [devices, data]);

  useEffect(() => {
    if ( preprocessing.length > 0 ) {
      let dataset: any[] = [];
      preprocessing.forEach((values) => {
        let sets = values;
        let waterQualities = values.waterQualities;
        let predictions = values.waterQualityPredictions;
        waterQualities = waterQualities.sort((a: any, b: any) => {
          return a.channel - b.channel;
        });
        predictions = predictions.sort((a: any, b: any) => {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        });
        sets.waterQualities = waterQualities;
        sets.waterQualityPredictions = predictions;
        dataset.push(sets);
      });
      dataset = dataset.sort((a: any, b: any) => {
        return a.number - b.number;
      });
      setProcessed(dataset);
    }
  }, [ preprocessing ]);

  const handleRecently = (id: string) => {
    const start = timezones(moment().utc().subtract(479, 'minutes').format(), 'YYYY-MM-DDTHH:mm:ss');
    const end = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');
    getRecentlyDevices(id, setRecently, start, end)
      .then((response) => {
        setRecently({ ...response, deviceId: id });
      })
      .catch(() => {
        setRecently({ waterQualities: [], waterQualityPredictions: [], waterTemperatures: [], deviceId: id });
      });
  };

  return (
    <RealTimeContent>
      { processed.map((value, index) => (
        <LocationChart key={index}>
          <div>
            <p>{ value.location }</p>
            <div>
              <p>{ timezones(value.acquisitionAt, 'YYYY-MM-DD HH:mm:ss') }</p>
              <button onClick={() => handleRecently(value.iotDeviceId)}><Recently /></button>
            </div>
          </div>

          <div>
            <p>센서 측정 값</p>
            <div>
              { value.waterQualities.map((sensor: any) => (
                <div key={sensor.waterQualitySensorId}>
                  <p className={sensor.alarm !== 'NORMAL' ? 'alarm' : ''}>{ sensor.name }</p>
                  { sensor.measuredValue !== null ? (
                    <div>
                      <p>{ sensor.measuredValue }</p>
                      <span>{ sensor.unit }</span>
                    </div>
                  ) : (
                    <div>
                      <p></p>
                    </div>
                  )}
                </div>
              ))}
              <div>
                <p>{ group.waterTemperature }</p>
                <div>
                  <p>{ value.waterTemperature }</p>
                  <span>{ group.waterTemperatureUnit }</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p>AI 예측 값</p>
            <div>
              { value.waterQualityPredictions.map((prediction: any) => (
                <div key={prediction.waterQualityPredictionId}>
                  <p className={ prediction.alarm && prediction.alarm !== 'NORMAL' ? 'alarm' : '' }>{ prediction.name }</p>
                  { prediction.predictionValue && prediction.predictionValue !== null ? (
                    <div>
                      <p>{ prediction.predictionValue }</p>
                      <span>{ prediction.unit }</span>
                    </div>
                  ) : (
                    <div>
                      <p></p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </LocationChart>
      ))}
    </RealTimeContent>
  );
}